import * as THREE from 'three'
import * as BufferGeometryUtils from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { MeshBVH } from 'three-mesh-bvh'
import { NidoCage, NidoMetalCage, NidoWoodenCage } from './parts/NidoCage'
import Resources from '~/service/experience/utils/Resources'
import NidoPart, {
  NidoSavedParts,
  type NidoPartParameters,
} from '~/service/experience/world/nido/parts/NidoPart'
import {
  NidoId,
  NidoPartName,
  NidoPosition,
  NidoCode,
  NidoSize,
  Axis,
  AxisPosition,
  NidoSocketType,
  NidoSocketCode,
  NidoSocketId,
  NidoCageType,
} from '~/service/experience/world/nido/enum'
import { NidoSeat } from '~/service/experience/world/nido/parts/NidoTexturePart'
import NidoTable from '~/service/experience/world/nido/parts/NidoTable'
import { Point } from '~/service/experience/world/nido/Point'
import NidoSocket from '~/service/experience/world/nido/parts/NidoSocket'

import { shallowRef } from 'vue'
import NidoAddButton from '~/components/NidoAddButton.vue'
import NidoConfigureButton from '~/components/NidoConfigureButton.vue'
import ModalConfigureNidoCorner from '~/components/ModalConfigureNidoCorner.vue'
import {
  PointType,
  NidoCornerSize,
  NidoType,
  NidoPlacementSize,
  NidoSeatsCount,
} from '~/service/experience/world/nido/enum'

import {
  NidoTripleSize,
  NidoDoubleSize,
} from '~/service/experience/world/nido/enum'
import ModalConfigureNido from '~/components/ModalConfigureNido.vue'
import {
  NidoSingleSize,
  NidoSingleModuleSize,
} from '~/service/experience/world/nido/enum'

export type Scales = {
  lowX: number
  highX: number
  y: number
  lowZ: number
  highZ: number
}

export abstract class Nido {
  id: string
  nidoCode: NidoCode
  nidoId: NidoId
  seatCategory = 116
  resources: Resources
  instance: THREE.Group
  collider: THREE.Mesh<THREE.BufferGeometry, THREE.MeshStandardMaterial>
  scene: THREE.Scene
  points: { [key: string]: Point }
  axis: Axis
  parts: {
    bottom: NidoCage
    left?: NidoSeat | NidoTable
    center?: NidoSeat | NidoTable
    right?: NidoSeat | NidoTable
  }

  component: any

  abstract remove(): void
  abstract resetNeighbor(
    axisPosition: AxisPosition,
    axis: Axis,
    axisCoordinate?: number
  ): void

  abstract addNeighbor(nido: Nido): void
  abstract toJSON(): string
  abstract getParts(): NidoPart[]
  abstract toggleAddButton(position: NidoPosition, value: boolean): void
  abstract canAddLowbackrest(position: NidoPosition): boolean
  abstract hasSocket(): boolean
  abstract changeSocketType(newSocketType: NidoSocketType): void
  abstract getRealScales(): Scales
  abstract getPlacementScales(): Scales
  abstract setCage(): void

  protected constructor(id: string, axis: Axis, component: any) {
    const configurator = useConfigurator()

    this.id = id
    this.axis = axis
    this.resources = configurator.getResources()
    this.scene = configurator.scene
    this.component = component
  }

  addCollider() {
    if (this.collider) {
      this.scene.remove(this.collider)
    }

    // collect all geometries to merge
    const geometries: THREE.BufferGeometry[] = []
    this.instance.updateMatrixWorld(true)
    this.instance.traverse((c: THREE.Object3D) => {
      if (c instanceof THREE.Mesh) {
        const cloned = c.geometry.clone()
        cloned.applyMatrix4(c.matrixWorld)
        for (const key in cloned.attributes) {
          if (key !== 'position') {
            cloned.deleteAttribute(key)
          }
        }

        geometries.push(cloned)
      }
    })

    // create the merged geometry
    const mergedGeometry = BufferGeometryUtils.mergeGeometries(
      geometries,
      false
    )
    mergedGeometry.boundsTree = new MeshBVH(mergedGeometry)

    this.collider = new THREE.Mesh(
      mergedGeometry,
      new THREE.MeshStandardMaterial()
    )
    this.collider.material.wireframe = true
    this.collider.material.opacity = 0.5
    this.collider.material.transparent = true
    this.collider.visible = false

    this.scene.add(this.collider)
  }

  getScalesWithoutParts(width: number, depth: number) {
    let scales: Scales

    const halfWidth = width / 2
    const halfDepth = depth / 2
    if (this.axis === Axis.X) {
      scales = {
        lowX: -halfWidth,
        highX: halfWidth,
        y: 0.0,
        lowZ: -halfDepth,
        highZ: halfDepth,
      }
    } else {
      scales = {
        lowX: -halfDepth,
        highX: halfDepth,
        y: 0.0,
        lowZ: -halfWidth,
        highZ: halfWidth,
      }
    }
    return scales
  }

  getPartScales(
    part: NidoSeat | NidoTable | NidoCage,
    initialScales: Scales,
    scales: Scales,
    hasNeighbourLeft = false,
    hasNeighbourRight = false,
    hasNeighbourTop = false,
    hasNeighbourBottom = false
  ) {
    if (part instanceof NidoCage) return

    if (part instanceof NidoSeat) {
      scales.y =
        scales.y < NidoSize.DEFAULT_HEIGHT ? NidoSize.DEFAULT_HEIGHT : scales.y
      // update height (y) of initial scales because we have multiple types of parts which could change height
      if (initialScales.y === 0 || initialScales.y === NidoSize.TABLE_HEIGHT) {
        initialScales.y = scales.y
      }

      if (part.lowBackrest) {
        scales.y =
          scales.y < initialScales.y + NidoSize.LOW_BACKREST_HEIGHT
            ? initialScales.y + NidoSize.LOW_BACKREST_HEIGHT
            : scales.y

        if (
          part.lowBackrest.isCorner &&
          part.position === NidoPosition.CENTER &&
          !part.lowBackrest.isInner
        ) {
          if (
            hasNeighbourLeft ||
            hasNeighbourRight ||
            hasNeighbourTop ||
            hasNeighbourBottom
          ) {
            const backrestWidth = NidoSize.BACKREST
            if (hasNeighbourLeft) {
              scales.highX = scales.highX + backrestWidth
            }
            if (hasNeighbourRight) {
              scales.lowX = scales.lowX - backrestWidth
            }
            if (hasNeighbourTop) {
              scales.highZ = scales.highZ + backrestWidth
            }
            if (hasNeighbourBottom) {
              scales.lowZ = scales.lowZ - backrestWidth
            }
          } else {
            // only straight module that can have corner in center is single
            const backrestHalfWidth = NidoSize.SINGLE_MODULE_BACKREST_WIDTH / 2
            scales.lowX = scales.lowX - backrestHalfWidth
            scales.highX = scales.highX + backrestHalfWidth
          }
        }

        if (this.axis === Axis.X) {
          if (part.lowBackrest.isCorner) {
            if (part.position === NidoPosition.LEFT) {
              scales.lowX = scales.lowX - NidoSize.BACKREST
            }
            if (part.position === NidoPosition.RIGHT) {
              scales.highX = scales.highX + NidoSize.BACKREST
            }
          }
          if (!part.lowBackrest.isInner) {
            if (part.inverted) {
              scales.highZ =
                scales.highZ < initialScales.highZ + NidoSize.BACKREST
                  ? initialScales.highZ + NidoSize.BACKREST
                  : scales.highZ
            } else {
              scales.lowZ =
                scales.lowZ > initialScales.lowZ - NidoSize.BACKREST
                  ? initialScales.lowZ - NidoSize.BACKREST
                  : scales.lowZ
            }
          }
        } else {
          if (part.lowBackrest.isCorner) {
            if (part.position === NidoPosition.LEFT) {
              scales.lowZ = scales.lowZ - NidoSize.BACKREST
            }
            if (part.position === NidoPosition.RIGHT) {
              scales.highZ = scales.highZ + NidoSize.BACKREST
            }
          }
          if (!part.lowBackrest.isInner) {
            if (part.inverted) {
              scales.lowX =
                scales.lowX > initialScales.lowX - NidoSize.BACKREST
                  ? initialScales.lowX - NidoSize.BACKREST
                  : scales.lowX
            } else {
              scales.highX =
                scales.highX < initialScales.highX + NidoSize.BACKREST
                  ? initialScales.highX + NidoSize.BACKREST
                  : scales.highX
            }
          }
        }

        if (part.lowBackrest.highBackrest) {
          scales.y =
            scales.y <
            initialScales.y +
              NidoSize.LOW_BACKREST_HEIGHT +
              NidoSize.HIGH_BACKREST_HEIGHT
              ? initialScales.y +
                NidoSize.LOW_BACKREST_HEIGHT +
                NidoSize.HIGH_BACKREST_HEIGHT
              : scales.y
        }
      }
    } else {
      scales.y =
        scales.y < NidoSize.TABLE_HEIGHT ? NidoSize.TABLE_HEIGHT : scales.y
      if (initialScales.y === 0) {
        initialScales.y = scales.y
      }
    }
  }

  changeSocketTypes(
    sockets: (NidoSocket | undefined)[],
    newSocketType: NidoSocketType
  ): void {
    sockets.forEach((socket: NidoSocket | undefined) => {
      if (socket === undefined) {
        return
      }
      socket.code = NidoSocketCode[newSocketType]
      socket.id = NidoSocketId[newSocketType]
    })
  }

  loadParts(parts: NidoSavedParts) {
    const partsTmp = <
      {
        left: NidoSeat | NidoTable
        center: NidoSeat | NidoTable
        right: NidoSeat | NidoTable
        bottom: NidoCage
      }
    >{}

    const partPositions = Object.keys(parts) as NidoPosition[]

    partPositions.forEach((partPosition) => {
      const part = parts[partPosition]

      if (!part) return

      if (part.type === NidoPartName.SEAT) {
        partsTmp[partPosition] = new NidoSeat(
          this,
          this.instance,
          this.resources,
          part.textureId,
          part,
          part.lowBackrest,
          part.socket
        )
      } else if (part.type === NidoPartName.TABLE) {
        partsTmp[partPosition] = new NidoTable(
          this,
          this.instance,
          this.resources,
          part,
          part.lowBackrest,
          part.socket
        )
      } else if (part.type === NidoPartName.CAGE) {
        if (part.material === 'metal') {
          partsTmp[partPosition] = new NidoMetalCage(
            this,
            this.instance,
            part,
            part.cageType
          )
        } else if (part.material === 'wood') {
          partsTmp[partPosition] = new NidoWoodenCage(
            this,
            this.instance,
            part,
            part.cageType
          )
        }
      }
    })

    return partsTmp
  }
}

const nidoAddButton = shallowRef(NidoAddButton)
const nidoConfigureButton = shallowRef(NidoConfigureButton)

const BUTTON_DISTANCE = 0.4

export class NidoCorner extends Nido {
  points: {
    ADD_X: Point
    ADD_Z: Point
    CONFIGURE: Point
  }

  invertedX: boolean
  invertedZ: boolean
  neighborX?: Nido
  neighborZ?: Nido

  parts: {
    center: NidoSeat | NidoTable
    bottom: NidoCage
  }

  constructor(
    id: string,
    position: { x: number; y: number; z: number },
    axis: Axis,
    invertedX: boolean,
    invertedZ: boolean,
    parts?: NidoSavedParts,
    seatCategory?: number
  ) {
    super(id, axis, ModalConfigureNidoCorner)

    this.nidoCode = NidoCode.CORNER
    this.nidoId = NidoId.CORNER
    this.seatCategory = seatCategory || 116

    let pointXOffset = 0
    let pointZOffset = 0

    if (invertedX && invertedZ) {
      pointXOffset = -BUTTON_DISTANCE
      pointZOffset = -BUTTON_DISTANCE
    } else if (invertedX) {
      pointXOffset = -BUTTON_DISTANCE
      pointZOffset = BUTTON_DISTANCE
    } else if (invertedZ) {
      pointXOffset = BUTTON_DISTANCE
      pointZOffset = -BUTTON_DISTANCE
    } else {
      pointXOffset = BUTTON_DISTANCE
      pointZOffset = BUTTON_DISTANCE
    }

    this.points = {
      CONFIGURE: new Point(
        new THREE.Vector3(position.x, position.y + 0.5, position.z),
        nidoConfigureButton,
        true,
        id,
        PointType.CONFIG
      ),
      ADD_X: new Point(
        new THREE.Vector3(
          position.x + pointXOffset,
          position.y + 0.35,
          position.z
        ),
        nidoAddButton,
        true,
        id,
        PointType.ADD
      ),
      ADD_Z: new Point(
        new THREE.Vector3(
          position.x,
          position.y + 0.35,
          position.z + pointZOffset
        ),
        nidoAddButton,
        true,
        id,
        PointType.ADD
      ),
    }

    this.instance = new THREE.Group()

    this.instance.position.set(position.x, position.y, position.z)

    // TODO: Init parts
    if (parts) {
      const partsTmp = this.loadParts(parts)

      this.parts = {
        ...partsTmp,
      }
    } else {
      const bottom = createNidoCage(
        this,
        this.instance,
        { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
        NidoCode.CAGE_CORNER
      )

      this.parts = {
        center: new NidoSeat(
          this,
          this.instance,
          this.resources,
          94,
          {
            offset: 0,
            inverted: false,
            position: NidoPosition.CENTER,
          },
          false
        ),
        bottom,
      }
    }

    this.invertedX = invertedX
    this.invertedZ = invertedZ

    this.setModelRotation()

    this.scene.add(this.instance)

    this.addCollider()
  }

  invert(axis: Axis) {
    if (axis === Axis.Z) {
      this.invertedZ = !this.invertedZ
    } else if (axis === Axis.X) {
      this.invertedX = !this.invertedX
    }

    this.setModelRotation()

    this.addCollider()
  }

  setModelRotation() {
    if (!this.invertedX && !this.invertedZ) {
      this.instance.rotation.y = 0
      this.points.ADD_Z.position.z = this.instance.position.z + BUTTON_DISTANCE
      this.points.ADD_X.position.x = this.instance.position.x + BUTTON_DISTANCE
    } else if (!this.invertedX && this.invertedZ) {
      this.instance.rotation.y = Math.PI / 2
      this.points.ADD_Z.position.z = this.instance.position.z - BUTTON_DISTANCE
      this.points.ADD_X.position.x = this.instance.position.x + BUTTON_DISTANCE
    } else if (this.invertedX && !this.invertedZ) {
      this.instance.rotation.y = (Math.PI * 3) / 2
      this.points.ADD_Z.position.z = this.instance.position.z + BUTTON_DISTANCE
      this.points.ADD_X.position.x = this.instance.position.x - BUTTON_DISTANCE
    } else if (this.invertedX && this.invertedZ) {
      this.instance.rotation.y = Math.PI
      this.points.ADD_Z.position.z = this.instance.position.z - BUTTON_DISTANCE
      this.points.ADD_X.position.x = this.instance.position.x - BUTTON_DISTANCE
    }
  }

  remove() {
    if (this.neighborX) {
      this.neighborX.resetNeighbor(
        AxisPosition.X,
        this.axis,
        this.instance.position.x
      )
    }
    if (this.neighborZ) {
      this.neighborZ.resetNeighbor(
        AxisPosition.Z,
        this.axis,
        this.instance.position.z
      )
    }
  }

  resetNeighbor(axisPosition: AxisPosition, axis: Axis) {
    if (axisPosition === AxisPosition.X) {
      this.neighborX = undefined
      this.points.ADD_X.active = true
    } else if (axisPosition === AxisPosition.Z) {
      this.neighborZ = undefined
      this.points.ADD_Z.active = true
    } else if (axis === Axis.X) {
      this.neighborX = undefined
      this.points.ADD_X.active = true
    } else {
      this.neighborZ = undefined
      this.points.ADD_Z.active = true
    }
  }

  addNeighbor(nido: Nido) {
    if (nido.axis === Axis.X) {
      this.neighborX = nido
      this.points.ADD_X.active = false
    } else if (nido.axis === Axis.Z) {
      this.neighborZ = nido
      this.points.ADD_Z.active = false
    }
  }

  getParts() {
    return [this.parts.bottom.getParts(), this.parts.center.getParts()]
      .flat(Infinity)
      .filter((part) => part !== undefined) as NidoPart[]
  }

  hasSocket(): boolean {
    return this.parts.center.socket !== undefined
  }

  changeSocketType(newSocketType: NidoSocketType): void {
    const socket = this.parts.center.socket
    this.changeSocketTypes([socket], newSocketType)
  }

  getRealScales(): Scales {
    let hasNeighbourLeft = false
    let hasNeighbourRight = false
    let hasNeighbourTop = false
    let hasNeighbourBottom = false

    if (this.axis === Axis.X) {
      if (this.invertedX) {
        hasNeighbourLeft = this.neighborX !== undefined
      } else {
        hasNeighbourRight = this.neighborX !== undefined
      }
      if (this.invertedZ) {
        hasNeighbourTop = this.neighborZ !== undefined
      } else {
        hasNeighbourBottom = this.neighborZ !== undefined
      }
    } else {
      if (this.invertedX) {
        hasNeighbourLeft = this.neighborX !== undefined
      } else {
        hasNeighbourRight = this.neighborX !== undefined
      }
      if (this.invertedZ) {
        hasNeighbourTop = this.neighborZ !== undefined
      } else {
        hasNeighbourBottom = this.neighborZ !== undefined
      }
    }

    const scales = this.getScalesWithoutParts(
      NidoCornerSize.WIDTH,
      NidoCornerSize.DEPTH
    )
    if (hasNeighbourLeft) {
      scales.lowX += NidoSize.NEIGHBOUR_SQUEEZE
    }
    if (hasNeighbourRight) {
      scales.highX -= NidoSize.NEIGHBOUR_SQUEEZE
    }
    if (hasNeighbourTop) {
      scales.lowZ += NidoSize.NEIGHBOUR_SQUEEZE
    }
    if (hasNeighbourBottom) {
      scales.highZ -= NidoSize.NEIGHBOUR_SQUEEZE
    }
    const initialScales = { ...scales }
    for (const part of Object.values(this.parts)) {
      this.getPartScales(
        part,
        initialScales,
        scales,
        hasNeighbourLeft,
        hasNeighbourRight,
        hasNeighbourTop,
        hasNeighbourBottom
      )
    }

    return scales
  }

  getPlacementScales(): Scales {
    const scales = this.getScalesWithoutParts(
      NidoPlacementSize.UNIT * NidoSeatsCount.CORNER,
      NidoPlacementSize.UNIT
    )
    const initialScales = { ...scales }
    for (const part of Object.values(this.parts)) {
      this.getPartScales(part, initialScales, scales)
    }

    return scales
  }

  canAddLowbackrest(position: NidoPosition) {
    if (position === NidoPosition.CENTER) {
      if (!this.neighborX || !this.neighborZ) {
        return false
      }

      return this.checkNeighborX() && this.checkNeighborZ()
    }

    return true
  }

  checkNeighborX() {
    if (this.neighborX) {
      if (
        this.neighborX instanceof NidoSingle &&
        this.neighborX.parts.center instanceof NidoSeat
      ) {
        return !this.neighborX.parts.center.lowBackrest
      }
      if (
        this.neighborX.parts.right instanceof NidoSeat &&
        this.neighborX.instance.position.x < this.instance.position.x
      ) {
        return !this.neighborX.parts.right.lowBackrest
      } else if (
        this.neighborX.parts.left instanceof NidoSeat &&
        this.neighborX.instance.position.x > this.instance.position.x
      ) {
        return !this.neighborX.parts.left.lowBackrest
      }

      return false
    }

    return true
  }

  checkNeighborZ() {
    if (this.neighborZ) {
      if (
        this.neighborZ instanceof NidoSingle &&
        this.neighborZ.parts.center instanceof NidoSeat
      ) {
        return !this.neighborZ.parts.center.lowBackrest
      }
      if (
        this.neighborZ.parts.right instanceof NidoSeat &&
        this.neighborZ.instance.position.z < this.instance.position.z
      ) {
        return !this.neighborZ.parts.right.lowBackrest
      } else if (
        this.neighborZ.parts.left instanceof NidoSeat &&
        this.neighborZ.instance.position.z > this.instance.position.z
      ) {
        return !this.neighborZ.parts.left.lowBackrest
      }

      return false
    }

    return true
  }

  setCage() {
    this.parts.bottom.remove()

    this.parts.bottom = createNidoCage(
      this,
      this.instance,
      { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
      NidoCode.CAGE_CORNER
    )
  }

  toJSON() {
    const id = this.id
    const nidoId = this.nidoId
    const nidoCode = this.nidoCode
    const neighborIds = [this.neighborX?.id, this.neighborZ?.id]
    const position = this.instance.position
    const axis = this.axis
    const invertedX = this.invertedX
    const invertedZ = this.invertedZ

    const type = NidoType.CORNER

    const center = JSON.parse(this.parts.center.toJSON())
    const bottom = JSON.parse(this.parts.bottom.toJSON())

    const parts = {
      center,
      bottom,
    }

    const obj = {
      id,
      nidoId,
      nidoCode,
      neighborIds,
      position,
      axis,
      invertedX,
      invertedZ,
      type,
      parts,
      seatCategory: this.seatCategory,
    }

    return JSON.stringify(obj)
  }

  toggleAddButton(position: NidoPosition, value: boolean): void {
    //
  }
}

export abstract class NidoStraight extends Nido {
  neighborLeft?: Nido
  neighborRight?: Nido

  points: {
    ADD_RIGHT: Point
    ADD_LEFT: Point
    CONFIGURE: Point
  }

  protected constructor(
    id: string,
    axis: Axis,
    component: any,
    position: {
      x: number
      y: number
      z: number
    },
    pointsConfig: {
      nidoConfigureButton: Component
      nidoAddButton: Component
      pointXOffset: number
      pointZOffset: number
    }
  ) {
    super(id, axis, component)

    this.points = {
      CONFIGURE: new Point(
        new THREE.Vector3(position.x, position.y + 0.5, position.z),
        pointsConfig.nidoConfigureButton,
        true,
        id,
        PointType.CONFIG
      ),
      ADD_RIGHT: new Point(
        new THREE.Vector3(
          position.x + pointsConfig.pointXOffset,
          position.y + 0.35,
          position.z + pointsConfig.pointZOffset
        ),
        pointsConfig.nidoAddButton,
        true,
        id,
        PointType.ADD
      ),
      ADD_LEFT: new Point(
        new THREE.Vector3(
          position.x - pointsConfig.pointXOffset,
          position.y + 0.35,
          position.z - pointsConfig.pointZOffset
        ),
        pointsConfig.nidoAddButton,
        true,
        id,
        PointType.ADD
      ),
    }

    this.instance = new THREE.Group()

    this.instance.position.set(position.x, position.y, position.z)

    if (this.axis === Axis.Z) {
      this.instance.rotation.y = -Math.PI / 2
    }

    this.scene.add(this.instance)
  }

  getScalesForWidthAndDepth(
    width: number,
    depth: number,
    hasNeighbourLeft = false,
    hasNeighbourRight = false
  ) {
    const scales = this.getScalesWithoutParts(width, depth)
    if (this.axis === Axis.X) {
      if (hasNeighbourLeft) {
        scales.lowX += NidoSize.NEIGHBOUR_SQUEEZE
      }
      if (hasNeighbourRight) {
        scales.highX -= NidoSize.NEIGHBOUR_SQUEEZE
      }
    } else {
      if (hasNeighbourLeft) {
        scales.lowZ += NidoSize.NEIGHBOUR_SQUEEZE
      }
      if (hasNeighbourRight) {
        scales.highZ -= NidoSize.NEIGHBOUR_SQUEEZE
      }
    }

    const initialScales = { ...scales }
    for (const part of Object.values(this.parts)) {
      this.getPartScales(part, initialScales, scales)
    }

    return scales
  }

  toJSON() {
    const id = this.id
    const nidoId = this.nidoId
    const nidoCode = this.nidoCode
    const neighborIds = [this.neighborLeft?.id, this.neighborRight?.id]
    const position = this.instance.position
    const axis = this.axis

    const obj = {
      id,
      nidoId,
      nidoCode,
      neighborIds,
      position,
      axis,
    }

    return JSON.stringify(obj)
  }

  remove() {
    if (this.neighborLeft) {
      this.neighborLeft.resetNeighbor(AxisPosition.LEFT, this.axis)
    }
    if (this.neighborRight) {
      this.neighborRight.resetNeighbor(AxisPosition.RIGHT, this.axis)
    }
  }

  resetNeighbor(
    axisPosition: AxisPosition,
    _axis: Axis,
    axisCoordinate: number
  ) {
    if (axisPosition === AxisPosition.LEFT) {
      this.neighborRight = undefined
      this.points.ADD_RIGHT.active = true
    } else if (axisPosition === AxisPosition.RIGHT) {
      this.neighborLeft = undefined
      this.points.ADD_LEFT.active = true
    } else if (axisPosition === AxisPosition.X) {
      if (axisCoordinate > this.instance.position.x) {
        this.neighborRight = undefined
        this.points.ADD_RIGHT.active = true
      } else {
        this.neighborLeft = undefined
        this.points.ADD_LEFT.active = true
      }
    } else if (axisPosition === AxisPosition.Z) {
      if (axisCoordinate > this.instance.position.z) {
        this.neighborRight = undefined
        this.points.ADD_RIGHT.active = true
      } else {
        this.neighborLeft = undefined
        this.points.ADD_LEFT.active = true
      }
    }
  }

  addNeighbor(nido: Nido) {
    if (this.axis === Axis.X) {
      if (nido.instance.position.x > this.instance.position.x) {
        this.neighborRight = nido
        this.points.ADD_RIGHT.active = false
      } else {
        this.neighborLeft = nido
        this.points.ADD_LEFT.active = false
      }
    } else if (this.axis === Axis.Z) {
      if (nido.instance.position.z > this.instance.position.z) {
        this.neighborRight = nido
        this.points.ADD_RIGHT.active = false
      } else {
        this.neighborLeft = nido
        this.points.ADD_LEFT.active = false
      }
    }
  }

  toggleAddButton(position: NidoPosition, value: boolean) {
    if (position === NidoPosition.RIGHT && !this.neighborRight) {
      this.points.ADD_RIGHT.active = value
    } else if (position === NidoPosition.LEFT && !this.neighborLeft) {
      this.points.ADD_LEFT.active = value
    } else if (
      position === NidoPosition.CENTER &&
      this instanceof NidoSingle &&
      !this.neighborLeft &&
      !this.neighborRight
    ) {
      this.points.ADD_LEFT.active = value
      this.points.ADD_RIGHT.active = value
    } else if (
      position === NidoPosition.CENTER &&
      this instanceof NidoSingle &&
      !this.neighborLeft &&
      this.neighborRight
    ) {
      this.points.ADD_LEFT.active = value
      this.points.ADD_RIGHT.active = false
    } else if (
      position === NidoPosition.CENTER &&
      this instanceof NidoSingle &&
      this.neighborLeft &&
      !this.neighborRight
    ) {
      this.points.ADD_LEFT.active = false
      this.points.ADD_RIGHT.active = value
    }
    const raycaster = useRaycaster()
    raycaster.checkPoints()
  }
}

const NIDO_SINGLE_BUTTON_DISTANCE = 0.4

function createNidoCage(
  parent: Nido,
  scene: THREE.Group,
  parameters: NidoPartParameters,
  cageSize: NidoCode
) {
  const { cageType } = useWorld()

  switch (cageSize) {
    case NidoCode.CAGE_SINGLE: {
      if (cageType === NidoCageType.WOOD) {
        parent.nidoCode = NidoCode.SINGLE_WOOD
        parent.nidoId = NidoId.SINGLE_WOOD
      } else if (cageType === NidoCageType.METAL) {
        parent.nidoCode = NidoCode.SINGLE
        parent.nidoId = NidoId.SINGLE
      }
      break
    }
    case NidoCode.CAGE_DOUBLE: {
      if (cageType === NidoCageType.WOOD) {
        parent.nidoCode = NidoCode.DOUBLE_WOOD
        parent.nidoId = NidoId.DOUBLE_WOOD
      } else if (cageType === NidoCageType.METAL) {
        parent.nidoCode = NidoCode.DOUBLE
        parent.nidoId = NidoId.DOUBLE
      }
      break
    }
    case NidoCode.CAGE_TRIPLE: {
      if (cageType === NidoCageType.WOOD) {
        parent.nidoCode = NidoCode.TRIPLE_WOOD
        parent.nidoId = NidoId.TRIPLE_WOOD
      } else if (cageType === NidoCageType.METAL) {
        parent.nidoCode = NidoCode.TRIPLE
        parent.nidoId = NidoId.TRIPLE
      }
      break
    }
    case NidoCode.CAGE_CORNER: {
      if (cageType === NidoCageType.WOOD) {
        parent.nidoCode = NidoCode.CORNER_WOOD
        parent.nidoId = NidoId.CORNER_WOOD
      } else if (cageType === NidoCageType.METAL) {
        parent.nidoCode = NidoCode.CORNER
        parent.nidoId = NidoId.CORNER
      }
      break
    }
  }

  return cageType === NidoCageType.METAL
    ? new NidoMetalCage(parent, scene, parameters, cageSize)
    : new NidoWoodenCage(parent, scene, parameters, cageSize)
}

export class NidoSingle extends NidoStraight {
  parts: {
    center: NidoSeat | NidoTable
    bottom: NidoCage
  }

  constructor(
    id: string,
    position: { x: number; y: number; z: number },
    axis: Axis,
    parts?: NidoSavedParts,
    seatCategory?: number
  ) {
    const pointXOffset = axis === Axis.X ? NIDO_SINGLE_BUTTON_DISTANCE : 0
    const pointZOffset = axis === Axis.X ? 0 : NIDO_SINGLE_BUTTON_DISTANCE

    super(id, axis, ModalConfigureNido, position, {
      nidoConfigureButton,
      nidoAddButton,
      pointZOffset,
      pointXOffset,
    })

    this.nidoCode = NidoCode.SINGLE
    this.nidoId = NidoId.SINGLE
    this.seatCategory = seatCategory || 116

    if (parts) {
      const partsTmp = this.loadParts(parts)

      this.parts = {
        ...partsTmp,
      }
    } else {
      const bottom = createNidoCage(
        this,
        this.instance,
        { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
        NidoCode.CAGE_SINGLE
      )

      this.parts = {
        center: new NidoSeat(this, this.instance, this.resources, 94, {
          offset: 0,
          inverted: false,
          position: NidoPosition.CENTER,
        }),
        bottom,
      }
    }

    this.addCollider()
  }

  getParts() {
    return [this.parts.bottom.getParts(), this.parts.center.getParts()]
      .flat(Infinity)
      .filter((part) => part !== undefined) as NidoPart[]
  }

  hasSocket(): boolean {
    return this.parts.center.socket !== undefined
  }

  changeSocketType(newSocketType: NidoSocketType) {
    const socket = this.parts.center.socket
    this.changeSocketTypes([socket], newSocketType)
  }

  getRealScales(): Scales {
    if (this.parts.center?.lowBackrest?.isCorner) {
      return this.getScalesForWidthAndDepth(
        NidoSingleModuleSize.WIDTH,
        NidoSingleModuleSize.DEPTH
      )
    }
    return this.getScalesForWidthAndDepth(
      NidoSingleSize.WIDTH,
      NidoSingleSize.DEPTH,
      this.neighborLeft !== undefined,
      this.neighborRight !== undefined
    )
  }

  getPlacementScales(): Scales {
    return this.getScalesForWidthAndDepth(
      NidoPlacementSize.UNIT * NidoSeatsCount.SINGLE,
      NidoPlacementSize.UNIT
    )
  }

  invert() {
    this.parts.center.invert()

    this.addCollider()
  }

  canAddLowbackrest(position: NidoPosition) {
    if (position === NidoPosition.CENTER) {
      if (
        this.neighborLeft &&
        this.neighborLeft instanceof NidoCorner &&
        this.neighborLeft.parts.center instanceof NidoSeat
      ) {
        return !this.neighborLeft.parts.center.lowBackrest?.isInner
      } else {
        return true
      }
    }

    return true
  }

  setCage() {
    this.parts.bottom.remove()

    this.parts.bottom = createNidoCage(
      this,
      this.instance,
      { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
      NidoCode.CAGE_SINGLE
    )
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    obj.type = NidoType.SINGLE
    obj.seatCategory = this.seatCategory
    const center = JSON.parse(this.parts.center.toJSON())
    const bottom = JSON.parse(this.parts.bottom.toJSON())

    obj.parts = {
      center,
      bottom,
    }

    return JSON.stringify(obj)
  }
}

const NIDO_DOUBLE_BUTTON_DISTANCE = 0.7
const NIDO_DOUBLE_OFFSET = 0.3

export class NidoDouble extends NidoStraight {
  parts: {
    left: NidoSeat | NidoTable
    right: NidoSeat | NidoTable
    bottom: NidoCage
  }

  constructor(
    id: string,
    position: { x: number; y: number; z: number },
    axis: Axis,
    parts?: NidoSavedParts,
    seatCategory?: number
  ) {
    const pointXOffset = axis === Axis.X ? NIDO_DOUBLE_BUTTON_DISTANCE : 0
    const pointZOffset = axis === Axis.X ? 0 : NIDO_DOUBLE_BUTTON_DISTANCE

    super(id, axis, ModalConfigureNido, position, {
      nidoConfigureButton,
      nidoAddButton,
      pointZOffset,
      pointXOffset,
    })

    this.nidoCode = NidoCode.DOUBLE
    this.nidoId = NidoId.DOUBLE
    this.seatCategory = seatCategory || 116

    if (parts) {
      const partsTmp = this.loadParts(parts)

      this.parts = {
        ...partsTmp,
      }
    } else {
      const bottom = createNidoCage(
        this,
        this.instance,
        { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
        NidoCode.CAGE_DOUBLE
      )

      this.parts = {
        left: new NidoSeat(this, this.instance, this.resources, 94, {
          offset: -NIDO_DOUBLE_OFFSET,
          inverted: false,
          position: NidoPosition.LEFT,
        }),
        right: new NidoSeat(this, this.instance, this.resources, 94, {
          offset: NIDO_DOUBLE_OFFSET,
          inverted: false,
          position: NidoPosition.RIGHT,
        }),
        bottom,
      }
    }

    this.addCollider()
  }

  invert(position: NidoPosition) {
    switch (position) {
      case NidoPosition.LEFT:
        this.parts.left.invert()
        break
      case NidoPosition.RIGHT:
        this.parts.right.invert()
        break
      default:
        return
    }

    this.addCollider()
  }

  getParts() {
    return [
      this.parts.bottom.getParts(),
      this.parts.left.getParts(),
      this.parts.right.getParts(),
    ]
      .flat(Infinity)
      .filter((part) => part !== undefined) as NidoPart[]
  }

  hasSocket(): boolean {
    const sockets = [this.parts.left.socket, this.parts.right.socket]
    return sockets.some((socket) => socket !== undefined)
  }

  changeSocketType(newSocketType: NidoSocketType): void {
    const sockets = [this.parts.left.socket, this.parts.right.socket]
    this.changeSocketTypes(sockets, newSocketType)
  }

  getRealScales(): Scales {
    return this.getScalesForWidthAndDepth(
      NidoDoubleSize.WIDTH,
      NidoDoubleSize.DEPTH,
      this.neighborLeft !== undefined,
      this.neighborRight !== undefined
    )
  }

  getPlacementScales(): Scales {
    return this.getScalesForWidthAndDepth(
      NidoPlacementSize.UNIT * NidoSeatsCount.DOUBLE,
      NidoPlacementSize.UNIT
    )
  }

  canAddLowbackrest(position: NidoPosition) {
    if (position === NidoPosition.LEFT) {
      if (
        this.neighborLeft &&
        this.neighborLeft instanceof NidoCorner &&
        this.neighborLeft.parts.center instanceof NidoSeat
      ) {
        return !this.neighborLeft.parts.center.lowBackrest?.isInner
      } else {
        return true
      }
    } else if (position === NidoPosition.RIGHT) {
      if (
        this.neighborRight &&
        this.neighborRight instanceof NidoCorner &&
        this.neighborRight.parts.center instanceof NidoSeat
      ) {
        return !this.neighborRight.parts.center.lowBackrest?.isInner
      } else {
        return true
      }
    }

    return false
  }

  setCage() {
    this.parts.bottom.remove()

    this.parts.bottom = createNidoCage(
      this,
      this.instance,
      { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
      NidoCode.CAGE_DOUBLE
    )
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    obj.type = NidoType.DOUBLE
    obj.seatCategory = this.seatCategory
    const left = JSON.parse(this.parts.left.toJSON())
    const right = JSON.parse(this.parts.right.toJSON())
    const bottom = JSON.parse(this.parts.bottom.toJSON())

    obj.parts = {
      left,
      right,
      bottom,
    }

    return JSON.stringify(obj)
  }
}

const NIDO_TRIPLE_BUTTON_DISTANCE = 1
const NIDO_TRIPLE_OFFSET = 0.6

export class NidoTriple extends NidoStraight {
  parts: {
    left: NidoSeat | NidoTable
    center: NidoSeat | NidoTable
    right: NidoSeat | NidoTable
    bottom: NidoCage
  }

  constructor(
    id: string,
    position: { x: number; y: number; z: number },
    axis: Axis,
    parts?: NidoSavedParts,
    seatCategory?: number
  ) {
    const pointXOffset = axis === Axis.X ? NIDO_TRIPLE_BUTTON_DISTANCE : 0
    const pointZOffset = axis === Axis.X ? 0 : NIDO_TRIPLE_BUTTON_DISTANCE

    super(id, axis, ModalConfigureNido, position, {
      nidoConfigureButton,
      nidoAddButton,
      pointZOffset,
      pointXOffset,
    })

    this.nidoCode = NidoCode.TRIPLE
    this.nidoId = NidoId.TRIPLE
    this.seatCategory = seatCategory || 116

    if (parts) {
      const partsTmp = this.loadParts(parts)

      this.parts = {
        ...partsTmp,
      }
    } else {
      const bottom = createNidoCage(
        this,
        this.instance,
        { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
        NidoCode.CAGE_TRIPLE
      )

      this.parts = {
        left: new NidoSeat(this, this.instance, this.resources, 94, {
          offset: -NIDO_TRIPLE_OFFSET,
          inverted: false,
          position: NidoPosition.LEFT,
        }),
        center: new NidoSeat(this, this.instance, this.resources, 94, {
          offset: 0,
          inverted: false,
          position: NidoPosition.CENTER,
        }),
        right: new NidoSeat(this, this.instance, this.resources, 94, {
          offset: NIDO_TRIPLE_OFFSET,
          inverted: false,
          position: NidoPosition.RIGHT,
        }),
        bottom,
      }
    }

    this.addCollider()
  }

  invert(part: NidoPosition) {
    switch (part) {
      case NidoPosition.LEFT:
        this.parts.left.invert()
        break
      case NidoPosition.CENTER:
        this.parts.center.invert()
        break
      case NidoPosition.RIGHT:
        this.parts.right.invert()
        break
      default:
        return
    }

    this.addCollider()
  }

  getParts() {
    return [
      this.parts.bottom.getParts(),
      this.parts.left.getParts(),
      this.parts.center.getParts(),
      this.parts.right.getParts(),
    ]
      .flat(Infinity)
      .filter((part) => part !== undefined) as NidoPart[]
  }

  hasSocket(): boolean {
    const sockets = [
      this.parts.left.socket,
      this.parts.center.socket,
      this.parts.right.socket,
    ]
    return sockets.some((socket) => socket !== undefined)
  }

  changeSocketType(newSocketType: NidoSocketType): void {
    const sockets = [
      this.parts.left.socket,
      this.parts.center.socket,
      this.parts.right.socket,
    ]
    this.changeSocketTypes(sockets, newSocketType)
  }

  getRealScales(): Scales {
    return this.getScalesForWidthAndDepth(
      NidoTripleSize.WIDTH,
      NidoTripleSize.DEPTH,
      this.neighborLeft !== undefined,
      this.neighborRight !== undefined
    )
  }

  getPlacementScales(): Scales {
    return this.getScalesForWidthAndDepth(
      NidoPlacementSize.UNIT * NidoSeatsCount.TRIPLE,
      NidoPlacementSize.UNIT
    )
  }

  canAddLowbackrest(position: NidoPosition) {
    if (position === NidoPosition.LEFT) {
      if (
        this.neighborLeft &&
        this.neighborLeft instanceof NidoCorner &&
        this.neighborLeft.parts.center instanceof NidoSeat
      ) {
        return !this.neighborLeft.parts.center.lowBackrest?.isInner
      } else {
        return true
      }
    } else if (position === NidoPosition.RIGHT) {
      if (
        this.neighborRight &&
        this.neighborRight instanceof NidoCorner &&
        this.neighborRight.parts.center instanceof NidoSeat
      ) {
        return !this.neighborRight.parts.center.lowBackrest?.isInner
      } else {
        return true
      }
    }

    return true
  }

  setCage() {
    this.parts.bottom.remove()

    this.parts.bottom = createNidoCage(
      this,
      this.instance,
      { offset: 0, inverted: false, position: NidoPosition.BOTTOM },
      NidoCode.CAGE_TRIPLE
    )
  }

  toJSON() {
    const json = super.toJSON()

    const obj = JSON.parse(json)

    obj.type = NidoType.TRIPLE
    obj.seatCategory = this.seatCategory
    const left = JSON.parse(this.parts.left.toJSON())
    const center = JSON.parse(this.parts.center.toJSON())
    const right = JSON.parse(this.parts.right.toJSON())
    const bottom = JSON.parse(this.parts.bottom.toJSON())

    obj.parts = {
      left,
      center,
      right,
      bottom,
    }

    return JSON.stringify(obj)
  }
}
